'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Lesson, M, MB, P, Quiz, Step, UL } from '../_components/lesson'
import * as EX from './exercises/frontier-extra'

const LoRAViz = dynamic(() => import('../_components/widgets/FrontierExtraWidgets').then(m => m.LoRAViz), { ssr: false })

export default function FineTuningAndLoRA() {
  return (
    <Lesson>
      <Step>
        <H>Teaching an old model new tricks</H>
        <P>
          Pretraining a large model costs millions. Adapting one doesn’t have to. <strong>Fine-tuning</strong> means continuing
          to train a pretrained model on a small, specific dataset: medical notes, your company’s support tickets, a particular
          style of writing. Everything the model already learned about language carries over. This is{' '}
          <strong>transfer learning</strong>, and it’s why one pretrained model can become thousands of specialised ones.
        </P>
        <UL>
          <li>
            <strong>Supervised fine-tuning (SFT)</strong>: train on example conversations written the way you want the model to
            answer. It’s the first step in turning a pretrained model into an assistant.
          </li>
          <li>
            <strong>Preference tuning</strong> (RLHF and its relatives): train on which answers people prefer. You met that in
            the previous lesson.
          </li>
        </UL>
      </Step>

      <Step gate={['chat-template', 'q-forget']}>
        <H>Conversations as training data</H>
        <P>
          To fine-tune a chat model, each conversation becomes one long text with special tokens marking who’s speaking. The
          loss only counts the assistant’s replies: we want the model to learn to <em>answer</em>, not to imitate the user.
        </P>
        <Exercise
          id="chat-template"
          title="formatChat and trainOn"
          {...EX.chatTemplate}
          hints={[
            <>
              <C>{'messages.map(m => `<|${m.role}|>${m.content}<|end|>\\n`).join("")'}</C>
            </>,
            <>
              <C>{'messages.map(m => m.role === "assistant")'}</C>
            </>,
          ]}
        />
        <Quiz
          id="q-forget"
          question="You fine-tune a general model for a few thousand steps on legal documents only. What’s the main risk?"
          options={[
            { label: 'It gets worse at everything else — “catastrophic forgetting”', correct: true },
            { label: 'It can no longer read legal text', why: 'It should get better at legal text. The danger is what it loses elsewhere.' },
            { label: 'Nothing: fine-tuning only adds knowledge', why: 'Every gradient step changes shared weights. Pushing hard on one task can overwrite others.' },
          ]}
          explain="Common fixes: a small learning rate, mixing some general data back in, or changing only a few new parameters — which brings us to LoRA."
        />
      </Step>

      <Step>
        <H>LoRA: change a little, cheaply</H>
        <P>
          Full fine-tuning updates every weight, so you need the same huge memory as pretraining, and you store a full copy of
          the model for every task. <strong>LoRA</strong> (Low-Rank Adaptation, 2021) freezes the original weights and learns a
          small correction for each matrix:
        </P>
        <MB>{'W\\,\\mathbf{x} \\;\\to\\; W\\,\\mathbf{x} + B A\\,\\mathbf{x} \\qquad A: r \\times d_{\\text{in}} \\quad B: d_{\\text{out}} \\times r'}</MB>
        <P>
          With a small rank <M>r</M> (often 8 or 16), <M>BA</M> has the full size of <M>W</M> but is built from very few numbers.
          The bet, which turns out to be right surprisingly often, is that adapting to a new task only needs changes along a few
          directions.
        </P>
        <Figure n="10.3" wide caption="The frozen matrix W plus a low-rank update B·A. Raise the rank or the layer width and watch how few of the numbers LoRA actually trains.">
          <LoRAViz />
        </Figure>
      </Step>

      <Step gate="lora-count">
        <H>Count the savings</H>
        <Exercise id="lora-count" title="loraParams and share" {...EX.loraCount} hints={[<><C>{'r * (dIn + dOut)'}</C>, and divide by <C>{'dIn * dOut'}</C> for the share.</>]} />
        <Callout kind="plain">
          Because the base model never changes, you can keep one copy of it and swap in a tiny LoRA file for each task. And
          because B starts at zero, training begins exactly at the original model.
        </Callout>
      </Step>

      <Step gate="lora-train">
        <H>Train a LoRA</H>
        <P>
          Here, a “pretrained” layer needs adapting to a new task that differs by a low-rank change. Only A and B go to the
          optimiser.
        </P>
        <Exercise
          id="lora-train"
          title="forward with a low-rank update"
          {...EX.loraTrain}
          hints={[
            <>
              <C>{'return x.matmul(W).add(x.matmul(A).matmul(B))'}</C>
            </>,
          ]}
        />
        <Callout kind="why">
          LoRA, and QLoRA (LoRA on top of a 4-bit quantised base model), made it possible to fine-tune models with billions of
          parameters on a single consumer GPU. Much of the open-model ecosystem is built on it.
        </Callout>
      </Step>
    </Lesson>
  )
}
